import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

declare global {
  interface Window {
    google?: any;
    googleTranslateElementInit?: () => void;
  }
}

type Lang = "nl" | "en";

function setCookie(name: string, value: string) {
  const host = window.location.hostname;
  const parts = host.split(".");
  const domain = parts.length > 1 ? "." + parts.slice(-2).join(".") : host;
  document.cookie = `${name}=${value};path=/;`;
  document.cookie = `${name}=${value};path=/;domain=${domain};`;
  document.cookie = `${name}=${value};path=/;domain=${host};`;
}

function clearCookie(name: string) {
  const host = window.location.hostname;
  const parts = host.split(".");
  const domain = parts.length > 1 ? "." + parts.slice(-2).join(".") : host;
  const expired = "Thu, 01 Jan 1970 00:00:00 GMT";
  document.cookie = `${name}=;expires=${expired};path=/;`;
  document.cookie = `${name}=;expires=${expired};path=/;domain=${domain};`;
  document.cookie = `${name}=;expires=${expired};path=/;domain=${host};`;
}

function loadGoogleTranslate() {
  if (document.getElementById("google-translate-script")) return;

  const hidden = document.createElement("div");
  hidden.id = "google_translate_element";
  hidden.style.display = "none";
  document.body.appendChild(hidden);

  window.googleTranslateElementInit = () => {
    new window.google.translate.TranslateElement(
      {
        pageLanguage: "nl",
        includedLanguages: "nl,en",
        autoDisplay: false,
      },
      "google_translate_element",
    );
  };

  const script = document.createElement("script");
  script.id = "google-translate-script";
  script.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
  script.async = true;
  document.body.appendChild(script);
}

export function LanguageSelector() {
  const [lang, setLang] = useState<Lang>("nl");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Init from cookie
    const match = document.cookie.match(/googtrans=\/[a-z]{2}\/([a-z]{2})/i);
    const isEn = match && match[1] === "en";
    if (isEn) {
      setLang("en");
      // EN user: laad Google Translate zodat pagina vertaald wordt
      loadGoogleTranslate();
    }
    // NL user: script wordt pas geladen bij openen dropdown of taalwissel

    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleOpen = () => {
    // Prefetch Google Translate zodra user interesse toont
    loadGoogleTranslate();
    setOpen((o) => !o);
  };


  const switchLang = (next: Lang) => {
    setLang(next);
    setOpen(false);
    if (next === "nl") {
      clearCookie("googtrans");
    } else {
      setCookie("googtrans", `/nl/${next}`);
    }
    window.location.reload();
  };

  const FlagNL = ({ className = "" }: { className?: string }) => (
    <svg viewBox="0 0 9 6" className={className} aria-hidden="true">
      <rect width="9" height="2" y="0" fill="#AE1C28" />
      <rect width="9" height="2" y="2" fill="#FFFFFF" />
      <rect width="9" height="2" y="4" fill="#21468B" />
    </svg>
  );

  const FlagGB = ({ className = "" }: { className?: string }) => (
    <svg viewBox="0 0 60 30" className={className} aria-hidden="true">
      <clipPath id="gb-c"><path d="M0,0 v30 h60 v-30 z" /></clipPath>
      <path d="M0,0 v30 h60 v-30 z" fill="#012169" />
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
      <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#gb-c)" stroke="#C8102E" strokeWidth="4" />
      <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
      <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
    </svg>
  );

  return (
    <div ref={ref} className="relative notranslate">
      <button
        type="button"
        onClick={handleOpen}
        className="flex items-center gap-1.5 text-sm text-foreground/70 transition-colors hover:text-orange"
        aria-label="Taal wijzigen"
      >
        {lang === "nl" ? <FlagNL className="h-3.5 w-5 rounded-sm" /> : <FlagGB className="h-3.5 w-5 rounded-sm" />}
        {lang === "nl" ? "NL" : "EN"}
        <ChevronDown className="size-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 min-w-[140px] overflow-hidden rounded-md border border-border bg-background shadow-lg">
          <button
            type="button"
            onClick={() => switchLang("nl")}
            className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted ${lang === "nl" ? "text-orange" : "text-foreground/80"}`}
          >
            <FlagNL className="h-3.5 w-5 rounded-sm" />
            Nederlands
          </button>
          <button
            type="button"
            onClick={() => switchLang("en")}
            className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted ${lang === "en" ? "text-orange" : "text-foreground/80"}`}
          >
            <FlagGB className="h-3.5 w-5 rounded-sm" />
            English
          </button>
        </div>
      )}
    </div>
  );
}
